import {useEffect, useState} from "react";

export interface ClientPagination<T> {
    /** 0-based, always within the current pages. */
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    pageItems: T[];
    totalItems: number;
}

/**
 * Pages a list that is already fully loaded, for lists that cannot be paged by the server (for
 * example ones saved back as a whole). Returns to the first page when resetKey changes, and stays
 * on the last page when items are removed from it.
 */
export const useClientPagination = <T, >(items: T[], pageSize: number, resetKey?: unknown): ClientPagination<T> => {
    const [requestedPage, setRequestedPage] = useState(0);
    const lastPage = Math.max(0, Math.ceil(items.length / pageSize) - 1);
    const pageNumber = Math.min(requestedPage, lastPage);

    useEffect(() => {
        setRequestedPage(0);
    }, [resetKey]);

    useEffect(() => {
        if (requestedPage > lastPage) {
            setRequestedPage(lastPage);
        }
    }, [requestedPage, lastPage]);

    return {
        pageNumber,
        setPageNumber: setRequestedPage,
        pageItems: items.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
        totalItems: items.length
    };
};
